import { Controller } from 'react-hook-form';
import {
  Select,
  InputText,
  InputNumber,
  TransactionTypeEnum,
  IconInfo,
  Checkbox,
} from '@ui/src';
import {
  ClientType,
  CounterPartyEnum,
  ScoreActionEnum,
  TransactionMonitoringRuleEntity,
} from '../../hoc/types';
import { TransactionMonitoringRuleFormTypes } from './types';

import cn from './form.module.css';

const TransactionMonitoringRuleForm = ({
  control,
  errors,
  ruleId,
  isEditable,
  entity,
  transactionType,
  paymentTypes,
  setValue,
  clearErrors,
}: TransactionMonitoringRuleFormTypes) => (
  <div className={cn.ruleForm}>
    <div className={cn.ruleFormBlockTitle}>General</div>
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="name"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="ruleName"
            placeholder="Enter Rule name"
            onChange={onChange}
            value={value}
            title="Rule name"
            error={errors?.name?.message}
            disabled={Boolean(ruleId && !isEditable)}
            tabIndex={ruleId && !isEditable ? -1 : 0}
          />
        )}
      />
    </div>
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="entity"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <>
            <Select
              list={[
                {
                  name: 'Transaction',
                  value: TransactionMonitoringRuleEntity.TRANSACTION,
                },
                {
                  name: 'Payment Order',
                  value: TransactionMonitoringRuleEntity.PAYMENT_ORDER,
                },
              ]}
              placeholder="Choose Rule Entity"
              name="entity"
              title="Rule Entity"
              value={value}
              onChange={(e) => {
                onChange(e);

                if (
                  e.target.value ===
                  TransactionMonitoringRuleEntity.PAYMENT_ORDER
                ) {
                  setValue('action', ScoreActionEnum.AML_REVIEW);

                  if (errors?.action?.message) {
                    clearErrors(['action']);
                  }
                } else {
                  setValue('action', '');
                }
              }}
              selectWrapperClassName={`${cn.ruleSelect} ${
                errors?.entity?.message ? cn.ruleSelectError : ''
              }`}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
              fullWidth
            />
            {errors?.entity?.message && (
              <div className={cn.error}>{errors?.entity?.message}</div>
            )}
          </>
        )}
      />
    </div>
    {(entity === TransactionMonitoringRuleEntity.TRANSACTION ||
      (ruleId && transactionType)) && (
      <div className={cn.ruleFormGroup}>
        <Controller
          control={control}
          name="transactionType"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <>
              <Select
                list={[
                  {
                    name: 'Internal',
                    value: TransactionTypeEnum.INTERNAL,
                  },
                  {
                    name: 'Incoming',
                    value: TransactionTypeEnum.INCOMING,
                  },
                  {
                    name: 'Outgoing',
                    value: TransactionTypeEnum.OUTGOING,
                  },
                ]}
                placeholder="Choose Transaction type"
                name="transactionType"
                title="Transaction type"
                value={value}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.transactionType?.message ? cn.ruleSelectError : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}
                fullWidth
              />
              {errors?.transactionType?.message && (
                <div className={cn.error}>
                  {errors?.transactionType?.message}
                </div>
              )}
            </>
          )}
        />
      </div>
    )}
    {(transactionType === TransactionTypeEnum.INCOMING ||
      transactionType === TransactionTypeEnum.OUTGOING) && (
      <div className={cn.ruleFormGroup}>
        <Controller
          control={control}
          name="paymentType"
          defaultValue={undefined}
          render={({ field: { onChange, value } }) => (
            <>
              <Select
                list={paymentTypes}
                placeholder="Choose Payment type"
                name="paymentType"
                title="Payment type"
                value={value as string}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.paymentType?.message ? cn.ruleSelectError : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}
                withClearButton
                fullWidth
              />
              {errors?.paymentType?.message && (
                <div className={cn.error}>{errors?.paymentType?.message}</div>
              )}
            </>
          )}
        />
      </div>
    )}
    {entity === TransactionMonitoringRuleEntity.PAYMENT_ORDER && (
      <div className={cn.ruleFormGroup}>
        <Controller
          control={control}
          name="paymentOrderType"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <>
              <Select
                list={[
                  {
                    name: 'Cash Collection Single',
                    value: 'CASH_COLLECTION_SINGLE_OUTPUT',
                  },
                ]}
                placeholder="Choose Payment Order type"
                name="paymentOrderType"
                title="Payment Order type"
                value={value}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.paymentOrderType?.message ? cn.ruleSelectError : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}
                fullWidth
              />
              {errors?.paymentOrderType?.message && (
                <div className={cn.error}>
                  {errors?.paymentOrderType?.message}
                </div>
              )}
            </>
          )}
        />
      </div>
    )}
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="clientType"
        defaultValue={undefined}
        render={({ field: { onChange, value } }) => (
          <>
            <Select
              list={[
                {
                  name: 'Individual',
                  value: ClientType.INDIVIDUAL,
                },
                {
                  name: 'Corporate',
                  value: ClientType.CORPORATE,
                },
              ]}
              placeholder="Choose Client type"
              name="clientType"
              title="Client type"
              value={value}
              onChange={onChange}
              selectWrapperClassName={`${cn.ruleSelect} ${
                errors?.clientType?.message ? cn.ruleSelectError : ''
              }`}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
              hintText={
                entity === TransactionMonitoringRuleEntity.PAYMENT_ORDER
                  ? 'For Payment Orders, client type is resolved automatically using the organization ID from the payment order or the recipient account.'
                  : ''
              }
              fullWidth
            />
            {errors?.clientType?.message && (
              <div className={cn.error}>{errors?.clientType?.message}</div>
            )}
          </>
        )}
      />
    </div>
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="counterParty"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <>
            <Select
              list={[
                {
                  name: 'Any',
                  value: CounterPartyEnum.ANY,
                },
                {
                  name: 'Same',
                  value: CounterPartyEnum.SAME,
                },
              ]}
              placeholder="Choose counterparty"
              name="counterParty"
              title="Counterparty"
              value={value}
              onChange={onChange}
              selectWrapperClassName={`${cn.ruleSelect} ${
                errors?.counterParty?.message ? cn.ruleSelectError : ''
              }`}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
              fullWidth
            />
            {errors?.counterParty?.message && (
              <div className={cn.error}>{errors?.counterParty?.message}</div>
            )}
          </>
        )}
      />
    </div>
    <div className={cn.ruleFormSeparator} />
    <div className={cn.ruleFormBlockTitle}>Conditions</div>
    {entity === TransactionMonitoringRuleEntity.PAYMENT_ORDER && (
      <div className={cn.paymentOrderHint}>
        <div className={cn.iconInfoWrapper}>
          <IconInfo width={16} height={16} />
        </div>
        <div className={cn.paymentOrderHintText}>
          Transactions will be considered related only if sender name, address,
          and date of birth are identical.
        </div>
      </div>
    )}
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="number"
        render={({ field: { onChange, value } }) => (
          <InputNumber
            name="number"
            placeholder="Enter Number of transactions"
            onChange={(e) => onChange(e)}
            value={value}
            title="Number of transactions"
            error={errors?.number?.message}
            labelClassName={`${cn.transactionNumbersInput} ${
              ruleId && !isEditable ? cn.inputWithPrefixDisaled : ''
            }`}
            prefix={<div className={cn.ruleTransactionNumberPrefix}>≥</div>}
            disabled={Boolean(ruleId && !isEditable)}
            tabIndex={ruleId && !isEditable ? -1 : 0}
          />
        )}
      />
    </div>
    <div className={`${cn.ruleFormGroup} ${cn.ruleFormGroupFlex}`}>
      <div className={cn.ruleFormGroupFlexInput}>
        <Controller
          control={control}
          name="volumeFrom"
          render={({ field: { onChange, value } }) => (
            <InputNumber
              name="volumeFrom"
              placeholder="Enter vol. from"
              onChange={(e) => {
                onChange(e);

                if (Number(e.target.value) > 999999999) {
                  onChange('999999999');
                }
              }}
              value={value}
              title="Transactions vol. from"
              error={errors?.volumeFrom?.message}
              prefix="From"
              prefixClassName={cn.volumePrefix}
              labelClassName={`${cn.transactionVolumeInput} ${
                ruleId && !isEditable ? cn.inputWithPrefixDisaled : ''
              }`}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.ruleFormGroupFlexInput}>
        <Controller
          control={control}
          name="volumeTo"
          render={({ field: { onChange, value } }) => (
            <InputNumber
              name="volumeTo"
              placeholder="Enter vol. to"
              onChange={(e) => {
                onChange(e);

                if (Number(e.target.value) > 999999999) {
                  onChange('999999999');
                }
              }}
              value={value}
              title="Transactions vol. to"
              error={errors?.volumeTo?.message}
              prefix="To"
              prefixClassName={cn.volumePrefix}
              labelClassName={`${cn.transactionVolumeInput} ${
                ruleId && !isEditable ? cn.inputWithPrefixDisaled : ''
              }`}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
    </div>
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="periodOfTimeInHours"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="periodOfTimeInHours"
            placeholder="Enter period"
            onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
            value={value}
            title="Period (in hours)"
            error={errors?.periodOfTimeInHours?.message}
            disabled={Boolean(ruleId && !isEditable)}
            tabIndex={ruleId && !isEditable ? -1 : 0}
          />
        )}
      />
    </div>
    <div className={cn.ruleFormSeparator} />
    <div className={cn.ruleFormBlockTitle}>Actions</div>
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="action"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <>
            {entity !== TransactionMonitoringRuleEntity.PAYMENT_ORDER && (
              <div className={cn.ruleFormRadioWrapper}>
                <Checkbox
                  type="radio"
                  title="OPS Review"
                  name="action"
                  onChange={() => onChange(ScoreActionEnum.OPS_REVIEW)}
                  checked={Boolean(value === ScoreActionEnum.OPS_REVIEW)}
                  visualClassName={
                    errors?.action?.message ? cn.checkboxError : ''
                  }
                />
              </div>
            )}
            <div className={cn.ruleFormRadioWrapper}>
              <Checkbox
                type="radio"
                title="AML review"
                name="action"
                onChange={() => onChange(ScoreActionEnum.AML_REVIEW)}
                checked={Boolean(value === ScoreActionEnum.AML_REVIEW)}
                visualClassName={
                  errors?.action?.message ? cn.checkboxError : ''
                }
              />
            </div>
            {errors?.action?.message && (
              <div className={cn.error}>{errors?.action?.message}</div>
            )}
          </>
        )}
      />
    </div>
  </div>
);

export default TransactionMonitoringRuleForm;
